import { Linkedin } from "lucide-react";

const FOUNDERS = [
  {
    name: "Prinkan Pal",
    role: "Co-founder & CEO",
    initials: "PP",
    linkedin: "#",
    bio: "Prinkan Pal is an AI and analytics leader with a proven track record of delivering high-impact enterprise solutions. At Intugle, Prinkan specializes in building and scaling data-driven products that transform complex business challenges into scalable, contextually intelligent AI applications.",
  },
  {
    name: "Manan Pachnanda",
    role: "Co-founder & CPO",
    initials: "MP",
    linkedin: "#",
    bio: "Manan Pachnanda is a strategy and AI leader with over 12 years of experience driving business transformation for Fortune 500 organizations. At Intugle, Manan works at the intersection of enterprise strategy, data engineering, semantic intelligence, and agentic AI to deliver scalable business outcomes.",
  },
  {
    name: "Jaskaran Singh",
    role: "Co-founder & CTO",
    initials: "JS",
    linkedin: "#",
    bio: "Jaskaran Singh is an AI and data science leader with extensive experience in machine learning, natural language processing, and enterprise intelligence. At Intugle, Jaskaran focuses on building context-aware AI systems that help organizations transform complex enterprise data into actionable knowledge and scalable AI solutions.",
  },
];

const Label = ({ children }) => (
  <div className="bg-[#211307] text-[#EDA13A] font-mono2 text-xs uppercase tracking-[0.28em] px-6 py-3">{children}</div>
);

// ---------- OPTION A: Light 3-card grid ----------
export const LeadershipA = () => (
  <section className="bg-[#F7F5F0] py-20 lg:py-28" data-testid="lead-a">
    <div className="max-w-7xl mx-auto px-6 sm:px-8">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4">Leadership</p>
      <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] text-[#3E2818] leading-[1.12] max-w-4xl">Meet the Founders</h2>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {FOUNDERS.map((f) => (
          <div key={f.name} className="bg-white rounded-2xl border border-[#3E2818]/10 p-8 flex flex-col">
            <div className="w-16 h-16 rounded-full bg-[#EFE7D9] flex items-center justify-center font-display text-xl font-medium text-[#C2410C]">{f.initials}</div>
            <h3 className="mt-6 font-display text-xl font-medium text-[#3E2818]">{f.name}</h3>
            <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#8A5730]">{f.role}</p>
            <p className="mt-4 text-[13px] text-[#3E2818]/65 leading-relaxed flex-1">{f.bio}</p>
            <a href={f.linkedin} className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#3E2818] hover:text-[#C2410C] transition-colors">
              <Linkedin size={16} /> Connect
            </a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- OPTION B: Dark editorial rows ----------
export const LeadershipB = () => (
  <section className="bg-[#211307] text-[#F7F5F0] py-20 lg:py-28" data-testid="lead-b">
    <div className="max-w-6xl mx-auto px-6 sm:px-8">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#EDA13A] mb-4">Leadership</p>
      <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] leading-[1.12] max-w-4xl">Meet the Founders</h2>
      <div className="mt-12">
        {FOUNDERS.map((f) => (
          <div key={f.name} className="grid md:grid-cols-[180px_1fr_auto] gap-6 md:gap-10 items-start py-9 border-t border-white/10">
            <div className="w-28 h-28 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 flex items-center justify-center font-display text-3xl font-medium text-[#EDA13A]">{f.initials}</div>
            <div>
              <h3 className="font-display text-2xl font-medium">{f.name}</h3>
              <p className="mt-1 font-mono2 text-[11px] uppercase tracking-[0.16em] text-[#EDA13A]">{f.role}</p>
              <p className="mt-4 text-sm text-white/65 leading-relaxed max-w-2xl">{f.bio}</p>
            </div>
            <a href={f.linkedin} className="shrink-0 w-11 h-11 rounded-full ring-1 ring-white/20 flex items-center justify-center text-white/70 hover:bg-[#EDA13A] hover:text-[#211307] hover:ring-[#EDA13A] transition-colors">
              <Linkedin size={18} />
            </a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------- OPTION C: Zig-zag portraits ----------
export const LeadershipC = () => (
  <section className="bg-[#F7F5F0] py-20 lg:py-28" data-testid="lead-c">
    <div className="max-w-6xl mx-auto px-6 sm:px-8">
      <p className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-[#8A5730] mb-4">Leadership</p>
      <h2 className="font-display font-medium tracking-tight text-2xl sm:text-3xl lg:text-[2.5rem] text-[#3E2818] leading-[1.12] max-w-4xl">Meet the Founders</h2>
      <div className="mt-14 space-y-14">
        {FOUNDERS.map((f, i) => (
          <div key={f.name} className={`grid md:grid-cols-[280px_1fr] gap-8 md:gap-14 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
            <div className="[direction:ltr] aspect-square rounded-2xl bg-gradient-to-br from-[#EFE7D9] to-[#E3D6C0] flex items-center justify-center font-display text-6xl font-medium text-[#C2410C]">{f.initials}</div>
            <div className="[direction:ltr]">
              <h3 className="font-display text-3xl font-medium text-[#3E2818]">{f.name}</h3>
              <p className="mt-2 font-mono2 text-xs uppercase tracking-[0.18em] text-[#8A5730]">{f.role}</p>
              <p className="mt-5 text-base text-[#3E2818]/70 leading-relaxed">{f.bio}</p>
              <a href={f.linkedin} className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#3E2818] hover:text-[#C2410C] transition-colors">
                <Linkedin size={16} /> Connect on LinkedIn
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function LeadershipOptions() {
  return (
    <div>
      <Label>Option A — Light 3-Card Grid</Label>
      <LeadershipA />
      <Label>Option B — Dark Editorial Rows</Label>
      <LeadershipB />
      <Label>Option C — Zig-zag Portraits</Label>
      <LeadershipC />
    </div>
  );
}
